import { useState } from 'react'
import {
  Building2,
  Calendar,
  Clock,
  Edit2,
  Phone,
  Trash2,
  Video,
} from 'lucide-react'
import {
  Controller,
  useFormState,
  type Control,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group'
import { SquareSettingsDialog } from '@/components/manager/calendars/SquareSettingsDialog'
import { ReconnectWixButton, WixSettingsDialog } from '@/components/manager/calendars/WixSettingsDialog'
import { ConnectCalendarDialog } from '@/components/manager/calendars/ConnectCalendarDialog'
import { WixIcon } from '@/components/manager/calendars/ProviderIcons'
import { ZoneVariables, versEcran, versServeur } from '@/components/manager/ZoneVariables'
import { useCalendars, wixDisconnected, type ConnectedCalendar } from '@/api/calendars'
import { usePlanRights } from '@/hooks/usePlanRights'
import { useCompanyPhoneNumbers } from '@/api/company'
import { WorkingHoursEditor, type WorkingHours } from './WorkingHoursEditor'
import type { FormValues } from './formSchema'

const GoogleIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
)

const MicrosoftIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
)

const SquareIcon = () => (
  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
    <rect x="2" y="2" width="20" height="20" rx="4" fill="#000000" />
    <rect x="8" y="8" width="8" height="8" rx="1.5" fill="#ffffff" />
  </svg>
)

const IconeFournisseur = ({ provider }: { provider: string }) =>
  provider === 'google' ? (
    <GoogleIcon />
  ) : provider === 'square' ? (
    <SquareIcon />
  ) : provider === 'wix' ? (
    <WixIcon className="w-4 h-4 shrink-0" />
  ) : (
    <MicrosoftIcon />
  )

/** A Wix calendar that must be reconnected says so next to its name. */
function EtatWix({ cal }: { cal: ConnectedCalendar }) {
  const { t } = useTranslation()
  if (cal.provider !== 'wix') return null
  return wixDisconnected(cal) ? (
    <span className="text-xs text-amber-700 dark:text-amber-300">{t('manager.calendars.status.disconnected')}</span>
  ) : (
    <span className="text-xs text-emerald-700 dark:text-emerald-400">{t('manager.calendars.status.connected')}</span>
  )
}

/**
 * Texte par defaut d'un modele, dans la langue de l'agent.
 *
 * Ces champs sont vides en base tant que le client n'a rien ecrit : le
 * backend resout le defaut a l'envoi. L'editeur affiche donc le meme texte
 * en placeholder, pour que « vide » ne se lise pas comme « rien ne partira ».
 */
function useTemplateDefault(agentLanguage: string) {
  return (key: string) =>
    i18n.getFixedT(agentLanguage === 'en' ? 'en' : 'fr')(
      `manager.aiAgents.calendar.defaults.${key}`,
    )
}

interface Props {
  control: Control<FormValues>
  watch: UseFormWatch<FormValues>
  setValue: UseFormSetValue<FormValues>
  companyId: number | undefined
  onNavigateToCalendars?: () => void
  /** Dans une ligne repliable : le titre est déjà affiché par le parent. */
  sansEntete?: boolean
}

export function CalendarBookingConfig({
  control,
  watch,
  setValue,
  companyId,
  onNavigateToCalendars,
  sansEntete = false,
}: Props) {
  const { t } = useTranslation()
  const { data: calendars = [] } = useCalendars(companyId)
  const { allowed } = usePlanRights()
  const { data: phoneData } = useCompanyPhoneNumbers(companyId)
  const companyNumbers = phoneData?.phone_numbers ?? []

  const [isConfigDrawerOpen, setIsConfigDrawerOpen] = useState(false)
  const [squareEnReglage, setSquareEnReglage] = useState(false)
  const [wixEnReglage, setWixEnReglage] = useState(false)
  const [connexionWix, setConnexionWix] = useState(false)
  // Connecting Wix leaves the page: unsaved changes to the agent would be lost.
  const { isDirty } = useFormState({ control })
  const [showWorkingHoursEditor, setShowWorkingHoursEditor] = useState(false)

  // Champs surveillés (re-render local à la modif).
  const googleCalendar = watch('google_calendar')
  const microsoftCalendar = watch('microsoft_calendar')
  const squareBooking = watch('square_booking')
  const wixBooking = watch('wix_booking')
  const bookingDuration = watch('calendar_booking_duration')
  const bufferTime = watch('calendar_buffer_time')
  const workingHours = watch('calendar_working_hours')
  const notifyCompany = watch('calendar_notify_company')
  const companyNotifyMethod = watch('calendar_company_notification_method')
  const canCancel = watch('can_cancel_appointments')
  const canReschedule = watch('can_reschedule_appointments')
  const confirmationMethod = watch('calendar_confirmation_method')
  const confirmationSms = confirmationMethod === 'sms' || confirmationMethod === 'both'
  const templateDefault = useTemplateDefault(watch('language'))

  const hasCalendar =
    googleCalendar != null || microsoftCalendar != null || squareBooking != null || wixBooking != null
  const selectedCalendarKey = googleCalendar
    ? `google_${googleCalendar}`
    : microsoftCalendar
      ? `microsoft_${microsoftCalendar}`
      : squareBooking
        ? `square_${squareBooking}`
        : wixBooking
          ? `wix_${wixBooking}`
          : null
  const selectedCalendar = calendars.find((c) => {
    if (googleCalendar && c.provider === 'google') return c.id === googleCalendar
    if (microsoftCalendar && c.provider === 'microsoft')
      return c.id === microsoftCalendar
    if (squareBooking && c.provider === 'square') return c.id === squareBooking
    if (wixBooking && c.provider === 'wix') return c.id === wixBooking
    return false
  })

  const estSquare = !!selectedCalendar?.square
  const estWix = !!selectedCalendar?.wix
  // Square and Wix decide the hours and the duration (the service's): the agent's own do not apply.
  const parService = estSquare || estWix
  const aucunWix = !calendars.some((c) => c.provider === 'wix')
  // Les variables des messages, nommées en clair à l'écran ; le serveur lit {id}.
  const variables = (ids: string[]) => ids.map((id) => ({ id, name: t(`manager.aiAgents.calendar.variables.${id}`) }))

  const setCalendarFromKey = (value: string) => {
    if (value === 'none') {
      setValue('google_calendar', null, { shouldDirty: true })
      setValue('microsoft_calendar', null, { shouldDirty: true })
      setValue('square_booking', null, { shouldDirty: true })
      setValue('wix_booking', null, { shouldDirty: true })
    } else {
      const [provider, idStr] = value.split('_')
      const id = parseInt(idStr, 10)
      setValue('google_calendar', provider === 'google' ? id : null, {
        shouldDirty: true,
      })
      setValue('microsoft_calendar', provider === 'microsoft' ? id : null, {
        shouldDirty: true,
      })
      setValue('square_booking', provider === 'square' ? id : null, {
        shouldDirty: true,
      })
      setValue('wix_booking', provider === 'wix' ? id : null, {
        shouldDirty: true,
      })
    }
  }

  // Shown wherever the calendar is picked, as long as no Wix site is connected.
  // Wix is on the Team plan: below it, the Calendars page shows the plan badge instead.
  const boutonConnecterWix = allowed('crm_equipe') ? (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={() => setConnexionWix(true)}
      className="gap-2 w-full sm:w-auto text-sm"
    >
      <WixIcon className="h-4 w-4" />
      {t('manager.aiAgents.calendar.connectWix')}
    </Button>
  ) : null

  const workingHoursSummary = () => {
    const hours = workingHours
    if (!hours) return t('manager.aiAgents.calendar.notConfigured')
    const enabled = (
      ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
    )
      .filter((d) => hours[d]?.enabled)
      .map((d) => ({ day: d, ...hours[d] }))
    if (enabled.length === 0)
      return t('manager.aiAgents.calendar.noActiveDays')
    if (enabled.length === 7)
      return t('manager.aiAgents.calendar.everyDay', {
        start: enabled[0].start,
        end: enabled[0].end,
      })
    const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
    if (
      enabled.length === 5 &&
      enabled.every((d) => weekdays.includes(d.day))
    ) {
      return t('manager.aiAgents.calendar.weekdays', {
        start: enabled[0].start,
        end: enabled[0].end,
      })
    }
    return t('manager.aiAgents.calendar.daysCount', { count: enabled.length })
  }

  return (
    <div>
      <div className={sansEntete ? 'flex justify-end mb-3' : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4'}>
        {!sansEntete && (
        <div>
          <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            {t('manager.aiAgents.calendar.title')}
          </h3>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 mt-1">
            {t('manager.aiAgents.calendar.subtitle')}
          </p>
        </div>
        )}

        {!hasCalendar && calendars.length > 0 && (
          <Sheet>
            <SheetTrigger asChild>
              <Button
                type="button"
                size="sm"
                className="gap-2 w-full sm:w-auto text-sm"
              >
                <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                {t('manager.aiAgents.calendar.activate')}
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>
                  {t('manager.aiAgents.calendar.activateTitle')}
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-4">
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  {t('manager.aiAgents.calendar.activateDescription')}
                </p>

                <div>
                  <Label htmlFor="select_calendar_sheet" className="text-sm font-medium mb-2 block">
                    {t('manager.aiAgents.calendar.calendarLabel')}
                  </Label>
                  <Select
                    value={selectedCalendarKey ?? 'none'}
                    onValueChange={(v) => {
                      setCalendarFromKey(v)
                      if (v !== 'none')
                        setTimeout(() => setIsConfigDrawerOpen(true), 300)
                    }}
                  >
                    <SelectTrigger id="select_calendar_sheet">
                      <SelectValue
                        placeholder={t('manager.aiAgents.calendar.pickCalendar')}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        {t('manager.aiAgents.calendar.none')}
                      </SelectItem>
                      {calendars.map((cal) => (
                        <SelectItem
                          key={`${cal.provider}_${cal.id}`}
                          value={`${cal.provider}_${cal.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <IconeFournisseur provider={cal.provider} />
                            <div className="flex flex-col">
                              <span className="font-medium">{cal.calendar_name}</span>
                              <span className="text-xs text-gray-500">
                                {cal.email} <EtatWix cal={cal} />
                              </span>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('manager.aiAgents.calendar.calendarsCount', {
                      count: calendars.length,
                    })}
                  </p>
                  {aucunWix && boutonConnecterWix && <div className="mt-3">{boutonConnecterWix}</div>}
                </div>

                <div className="pt-4">
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={() => onNavigateToCalendars?.()}
                    className="text-blue-600 p-0 h-auto"
                  >
                    {t('manager.aiAgents.calendar.manageCalendars')} →
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        )}

        {!hasCalendar && calendars.length === 0 && (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onNavigateToCalendars?.()}
              className="gap-2 w-full sm:w-auto text-sm"
            >
              <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              {t('manager.aiAgents.calendar.connectCalendar')}
            </Button>
            {boutonConnecterWix}
          </div>
        )}
      </div>

      {hasCalendar && (
        <div className="space-y-3">
          {/* Carte résumé */}
          <div className="border dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900 hover:border-blue-300 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-sm mb-1">
                  {selectedCalendar?.calendar_name ?? t('manager.aiAgents.calendar.none')}
                </h4>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-slate-400">
                  {/* Square et Wix : la durée est celle du service choisi, pas celle de l'agent. */}
                  <span>
                    {selectedCalendar?.square
                      ? selectedCalendar.square.service_name
                        ? `${selectedCalendar.square.service_name} · ${selectedCalendar.square.service_duration_minutes}min`
                        : t('manager.calendars.square.allServices', { count: selectedCalendar.square.services.length })
                      : selectedCalendar?.wix
                        ? selectedCalendar.wix.service_name
                          ? `${selectedCalendar.wix.service_name} · ${selectedCalendar.wix.service_duration_minutes}min`
                          : t('manager.calendars.wix.allServices', { count: selectedCalendar.wix.services.length })
                        : `${bookingDuration || 30}min`}
                  </span>
                  {estSquare && (
                    <span>• {t('manager.aiAgents.calendar.squareShort')}</span>
                  )}
                  {estSquare && selectedCalendar?.square?.last_error && (
                    <span className="text-amber-700 dark:text-amber-300">• {t(`manager.calendars.square.errors.${selectedCalendar.square.last_error}`)}</span>
                  )}
                  {estWix && selectedCalendar && (
                    <>
                      <span>• {t('manager.aiAgents.calendar.wixShort')}</span>
                      <span>• <EtatWix cal={selectedCalendar} /></span>
                    </>
                  )}
                  {!parService && (
                    <>
                      {bufferTime > 0 && (
                        <span>• {t('manager.aiAgents.calendar.bufferShort')}: {bufferTime}min</span>
                      )}
                      <span>• {workingHoursSummary()}</span>
                    </>
                  )}
                </div>
                {estWix && selectedCalendar && wixDisconnected(selectedCalendar) && (
                  <div className="mt-3 space-y-2">
                    {selectedCalendar.wix?.last_error === 'wix_app_removed' && (
                      <p className="text-xs text-amber-700 dark:text-amber-300">
                        {t('manager.calendars.wix.errors.wix_app_removed')}
                      </p>
                    )}
                    <ReconnectWixButton calendar={selectedCalendar} companyId={companyId} warnUnsaved={isDirty} />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsConfigDrawerOpen(true)}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setValue('google_calendar', null, { shouldDirty: true })
                    setValue('microsoft_calendar', null, { shouldDirty: true })
                    setValue('square_booking', null, { shouldDirty: true })
                    setValue('wix_booking', null, { shouldDirty: true })
                  }}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          </div>

          {/* Sheet config complète */}
          <Sheet open={isConfigDrawerOpen} onOpenChange={setIsConfigDrawerOpen}>
            <SheetContent
              side="right"
              className="w-full sm:w-[600px] p-0 overflow-hidden"
            >
              <div className="px-6 pt-6 pb-4 border-b dark:border-slate-800 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-500/10 dark:to-indigo-500/10">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 dark:bg-blue-500/20 rounded-lg">
                      <Calendar className="h-5 w-5 text-blue-600" />
                    </div>
                    {t('manager.aiAgents.calendar.title')}
                  </SheetTitle>
                </SheetHeader>
              </div>
              <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-140px)]">
                {/* Calendrier */}
                    <div>
                      <Label htmlFor="calendar_select">
                        {t('manager.aiAgents.calendar.calendarLabel')}
                      </Label>
                      <Select
                        value={selectedCalendarKey ?? 'none'}
                        onValueChange={setCalendarFromKey}
                      >
                        <SelectTrigger id="calendar_select">
                          <SelectValue
                            placeholder={t('manager.aiAgents.calendar.pickCalendar')}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">
                            {t('manager.aiAgents.calendar.none')}
                          </SelectItem>
                          {calendars.map((cal) => (
                            <SelectItem
                              key={`${cal.provider}_${cal.id}`}
                              value={`${cal.provider}_${cal.id}`}
                            >
                              <div className="flex items-center gap-2">
                                <IconeFournisseur provider={cal.provider} />
                                <span>{cal.calendar_name}</span>
                                <EtatWix cal={cal} />
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {aucunWix && <div className="mt-2">{boutonConnecterWix}</div>}
                    </div>

                {/* Ce que l'agent demande avant de réserver : vaut pour Square, Google et Microsoft. */}
                <Controller
                  control={control}
                  name="calendar_intake_info"
                  render={({ field }) => (
                    <div>
                      <Label htmlFor="calendar_intake_info" className="font-medium text-sm">
                        {t('manager.aiAgents.calendar.intakeInfo')}
                      </Label>
                      <Input
                        id="calendar_intake_info"
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        placeholder={t('manager.aiAgents.calendar.intakeInfoPh')}
                        className="mt-1.5"
                      />
                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                        {t('manager.aiAgents.calendar.intakeInfoHelp')}
                      </p>
                    </div>
                  )}
                />

                {estSquare ? (
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3 text-xs text-slate-600 dark:text-slate-300 space-y-2">
                    <p>{t('manager.aiAgents.calendar.squareNote')}</p>
                    <Button type="button" variant="outline" size="sm" onClick={() => setSquareEnReglage(true)}>
                      {t('manager.calendars.square.title')}
                    </Button>
                  </div>
                ) : estWix && selectedCalendar ? (
                  <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3 text-xs text-slate-600 dark:text-slate-300 space-y-2">
                    <p>{t('manager.aiAgents.calendar.wixNote')}</p>
                    {selectedCalendar.wix?.last_error === 'wix_app_removed' ? (
                      <ReconnectWixButton calendar={selectedCalendar} companyId={companyId} warnUnsaved={isDirty} />
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setWixEnReglage(true)}>
                          {t('manager.calendars.wix.title')}
                        </Button>
                        {wixDisconnected(selectedCalendar) && (
                          <ReconnectWixButton calendar={selectedCalendar} companyId={companyId} warnUnsaved={isDirty} />
                        )}
                      </div>
                    )}
                    {/* Deposit set on the service in Wix: a payment link goes in the confirmation text. */}
                    <Controller
                      control={control}
                      name="calendar_deposit_link"
                      render={({ field }) => (
                        <div className="flex items-start gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                          <input
                            type="checkbox"
                            id="calendar_deposit_link"
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            className="mt-0.5 rounded"
                          />
                          <div className="flex-1">
                            <Label htmlFor="calendar_deposit_link" className="cursor-pointer font-medium text-sm text-slate-800 dark:text-slate-100">
                              {t('manager.aiAgents.calendar.depositLink.label')}
                            </Label>
                            <p className="mt-0.5">{t('manager.aiAgents.calendar.depositLink.help')}</p>
                            {field.value && !confirmationSms && (
                              <p className="mt-1 text-amber-700 dark:text-amber-400">
                                {t('manager.aiAgents.calendar.depositLink.needsSms')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    />
                  </div>
                ) : (
                  <>
                    {/* Durée et battement, sur une ligne */}
                    <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="booking_duration">
                        {t('manager.aiAgents.calendar.duration')}
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="booking_duration"
                          type="number"
                          min={5}
                          step={5}
                          className="flex-1"
                          {...{ valueAsNumber: true }}
                          value={bookingDuration ?? ''}
                          onChange={(e) =>
                            setValue(
                              'calendar_booking_duration',
                              e.target.value === '' ? 30 : Number.parseInt(e.target.value, 10) || 30,
                              { shouldDirty: true },
                            )
                          }
                        />
                        <span className="text-sm text-gray-500">min</span>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="buffer_time">
                        {t('manager.aiAgents.calendar.buffer')}
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="buffer_time"
                          type="number"
                          min={0}
                          step={5}
                          className="flex-1"
                          value={bufferTime ?? ''}
                          onChange={(e) =>
                            setValue(
                              'calendar_buffer_time',
                              e.target.value === '' ? 0 : Number.parseInt(e.target.value, 10) || 0,
                              { shouldDirty: true },
                            )
                          }
                        />
                        <span className="text-sm text-gray-500">min</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {t('manager.aiAgents.calendar.bufferHelp')}
                      </p>
                    </div>
                    </div>

                    {/* Disponibilités */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>{t('manager.aiAgents.calendar.availabilitySection')}</Label>
                        {showWorkingHoursEditor && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => setShowWorkingHoursEditor(false)}>
                            {t('manager.aiAgents.calendar.reduce')}
                          </Button>
                        )}
                      </div>
                  {!showWorkingHoursEditor ? (
                    <>
                      <div className="text-sm text-gray-600 dark:text-slate-400 bg-gray-50 dark:bg-slate-800/50 p-3 rounded-lg">
                        {workingHoursSummary()}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowWorkingHoursEditor(true)}
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        {t('manager.aiAgents.calendar.editAvailability')}
                      </Button>
                    </>
                  ) : (
                    <div className="border dark:border-slate-700 rounded-lg p-4 bg-gray-50 dark:bg-slate-800/50">
                      <WorkingHoursEditor
                        value={workingHours as WorkingHours}
                        onChange={(next) =>
                          setValue('calendar_working_hours', next, {
                            shouldDirty: true,
                          })
                        }
                      />
                    </div>
                  )}
                    </div>
                  </>
                )}

                {/* Ce que l'agent peut faire */}
                <div className="space-y-3 pt-4 border-t dark:border-slate-800">
                  <Label>{t('manager.aiAgents.calendar.permissionsSection')}</Label>
                    <PermissionCheckbox
                      control={control}
                      name="can_find_appointments"
                      labelKey="canFind"
                    />
                    <PermissionCheckbox
                      control={control}
                      name="can_cancel_appointments"
                      labelKey="canCancel"
                      warning
                    />
                    <PermissionCheckbox
                      control={control}
                      name="can_reschedule_appointments"
                      labelKey="canReschedule"
                      warning
                    />
                  {(canCancel || canReschedule) && (
                    <p className="text-xs text-gray-500 dark:text-slate-400">{t('manager.aiAgents.calendar.securityNote')}</p>
                  )}
                </div>

                {/* Options : tout ce qui a un défaut raisonnable */}
                {!estSquare && (
                  <Collapsible className="pt-4 border-t dark:border-slate-800">
                    <CollapsibleTrigger className="text-sm font-medium text-blue-600 hover:text-blue-700">
                      {t('manager.aiAgents.calendar.options')}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-6 pt-4">
                      {/* Wix : le rendez-vous se passe au commerce, en personne. */}
                      {!estWix && (
                      <div className="space-y-3">
                        <Label>{t('manager.aiAgents.calendar.defaultTypeSection')}</Label>
                  <Controller
                    control={control}
                    name="calendar_default_appointment_type"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="phone">
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-blue-500" />
                              <span>{t('manager.aiAgents.calendar.typePhone')}</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="in_person">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-amber-500" />
                              <span>
                                {t('manager.aiAgents.calendar.typeInPerson')}
                              </span>
                            </div>
                          </SelectItem>
                          <SelectItem value="video">
                            <div className="flex items-center gap-2">
                              <Video className="h-4 w-4 text-purple-500" />
                              <span>{t('manager.aiAgents.calendar.typeVideo')}</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />

                  <Controller
                    control={control}
                    name="calendar_allow_client_type_choice"
                    render={({ field }) => (
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="allow_client_type_choice"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="mt-1 rounded"
                        />
                        <div className="flex-1">
                          <Label
                            htmlFor="allow_client_type_choice"
                            className="cursor-pointer font-medium text-sm"
                          >
                            {t('manager.aiAgents.calendar.allowClientChoice')}
                          </Label>
                          <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                            {t('manager.aiAgents.calendar.allowClientChoiceHelp')}
                          </p>
                        </div>
                      </div>
                    )}
                  />
                      </div>
                      )}

                      <div className="space-y-3">
                        <Label>{t('manager.aiAgents.calendar.clientNotificationsSection')}</Label>
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">
                      {t('manager.aiAgents.calendar.confirmationMethod')}
                    </Label>
                    <Controller
                      control={control}
                      name="calendar_confirmation_method"
                      render={({ field }) => (
                        <ToggleGroup
                          type="single"
                          value={field.value}
                          onValueChange={(v) => v && field.onChange(v)}
                        >
                          <ToggleGroupItem value="sms">SMS</ToggleGroupItem>
                          <ToggleGroupItem value="email">Email</ToggleGroupItem>
                          <ToggleGroupItem value="both">
                            {t('manager.aiAgents.calendar.both')}
                          </ToggleGroupItem>
                          <ToggleGroupItem value="none">
                            {t('manager.aiAgents.calendar.none')}
                          </ToggleGroupItem>
                        </ToggleGroup>
                      )}
                    />
                  </div>
                      </div>

                      <div className="space-y-3">
                        <Label>{t('manager.aiAgents.calendar.companyAlertsSection')}</Label>
                  <Controller
                    control={control}
                    name="calendar_notify_company"
                    render={({ field }) => (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="notify_company"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="rounded"
                        />
                        <Label htmlFor="notify_company" className="cursor-pointer">
                          {t('manager.aiAgents.calendar.notifyCompany')}
                        </Label>
                      </div>
                    )}
                  />

                  {notifyCompany && (
                    <>
                      <div>
                        <Label className="text-sm font-semibold mb-2 block">
                          {t('manager.aiAgents.calendar.companyNotifMethod')}
                        </Label>
                        <Controller
                          control={control}
                          name="calendar_company_notification_method"
                          render={({ field }) => (
                            <ToggleGroup
                              type="single"
                              value={field.value}
                              onValueChange={(v) => v && field.onChange(v)}
                            >
                              <ToggleGroupItem value="email">Email</ToggleGroupItem>
                              <ToggleGroupItem value="sms">SMS</ToggleGroupItem>
                              <ToggleGroupItem value="both">
                                {t('manager.aiAgents.calendar.both')}
                              </ToggleGroupItem>
                            </ToggleGroup>
                          )}
                        />
                      </div>

                      {(companyNotifyMethod === 'email' ||
                        companyNotifyMethod === 'both') && (
                        <div>
                          <Label htmlFor="company_email" className="text-sm font-medium">
                            {t('manager.aiAgents.calendar.companyEmail')}
                          </Label>
                          <Controller
                            control={control}
                            name="calendar_company_notification_email"
                            render={({ field }) => (
                              <Input
                                id="company_email"
                                type="email"
                                placeholder="contact@entreprise.com"
                                value={field.value ?? ''}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </div>
                      )}

                      {(companyNotifyMethod === 'sms' ||
                        companyNotifyMethod === 'both') && (
                        <div>
                          <Label htmlFor="company_phone" className="text-sm font-medium">
                            {t('manager.aiAgents.calendar.companyPhone')}
                          </Label>
                          <Controller
                            control={control}
                            name="calendar_company_notification_phone"
                            render={({ field }) => (
                              <select
                                id="company_phone"
                                value={field.value ?? ''}
                                onChange={(e) => field.onChange(e.target.value)}
                                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-900"
                              >
                                <option value="">
                                  {t('manager.aiAgents.calendar.pickNumber')}
                                </option>
                                {companyNumbers.map((n) => (
                                  <option key={n.id} value={n.phone_number}>
                                    {n.label
                                      ? `${n.label} (${n.phone_number})`
                                      : n.phone_number}
                                  </option>
                                ))}
                              </select>
                            )}
                          />
                        </div>
                      )}
                    </>
                  )}
                      </div>

                      {(confirmationSms || canCancel || canReschedule) && (
                        <Collapsible>
                          <CollapsibleTrigger className="text-sm text-slate-500 hover:text-slate-900 dark:hover:text-white">
                            {t('manager.aiAgents.calendar.customMessages')}
                          </CollapsibleTrigger>
                          <CollapsibleContent className="space-y-5 pt-3">
                            {confirmationSms && (
                              <MessageEditor
                                control={control}
                                titre={t('manager.aiAgents.calendar.customizeBookingSms')}
                                bodyName="calendar_sms_template"
                                defaultBody={templateDefault('bookingSms')}
                                variables={variables(['client_name', 'date', 'time', 'company_name', 'company_phone'])}
                                texto
                              />
                            )}
                            {canCancel && (
                              <MessageEditor
                                control={control}
                                titre={t('manager.aiAgents.calendar.customizeCancelEmail')}
                                subjectName="calendar_cancellation_email_subject"
                                bodyName="calendar_cancellation_email_body"
                                defaultSubject={templateDefault('cancellationEmailSubject')}
                                defaultBody={templateDefault('cancellationEmailBody')}
                                variables={variables(['agent_name', 'client_phone', 'appointment_date', 'appointment_time', 'company_name'])}
                              />
                            )}
                            {canReschedule && (
                              <>
                                <MessageEditor
                                  control={control}
                                  titre={t('manager.aiAgents.calendar.customizeRescheduleEmail')}
                                  subjectName="calendar_reschedule_email_subject"
                                  bodyName="calendar_reschedule_email_body"
                                  defaultSubject={templateDefault('rescheduleEmailSubject')}
                                  defaultBody={templateDefault('rescheduleEmailBody')}
                                  variables={variables(['agent_name', 'client_name', 'client_phone', 'old_date', 'old_time', 'new_date', 'new_time', 'company_name'])}
                                />
                                <MessageEditor
                                  control={control}
                                  titre={t('manager.aiAgents.calendar.customizeRescheduleSms')}
                                  bodyName="calendar_reschedule_sms_template"
                                  defaultBody={templateDefault('rescheduleSms')}
                                  variables={variables(['company_name', 'new_date', 'new_time'])}
                                  texto
                                />
                              </>
                            )}
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            </SheetContent>
          </Sheet>
          <SquareSettingsDialog
            calendar={squareEnReglage && selectedCalendar ? selectedCalendar : null}
            onClose={() => setSquareEnReglage(false)}
            companyId={companyId}
          />
          <WixSettingsDialog
            calendar={wixEnReglage && estWix && selectedCalendar ? selectedCalendar : null}
            onClose={() => setWixEnReglage(false)}
            companyId={companyId}
          />
        </div>
      )}
      <ConnectCalendarDialog
        open={connexionWix}
        onClose={() => setConnexionWix(false)}
        companyId={companyId}
        initialProvider="wix"
        warnUnsaved={isDirty}
      />
    </div>
  )
}

// =============================================================================
// Sous-composants
// =============================================================================

function PermissionCheckbox({
  control,
  name,
  labelKey,
  warning,
}: {
  control: Control<FormValues>
  name: 'can_find_appointments' | 'can_cancel_appointments' | 'can_reschedule_appointments'
  labelKey: 'canFind' | 'canCancel' | 'canReschedule'
  warning?: boolean
}) {
  const { t } = useTranslation()
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id={name}
            checked={!!field.value}
            onChange={(e) => field.onChange(e.target.checked)}
            className="mt-1 rounded"
          />
          <div className="flex-1">
            <Label htmlFor={name} className="cursor-pointer font-medium text-sm">
              {t(`manager.aiAgents.calendar.permissions.${labelKey}.label`)}
            </Label>
            {!warning && (
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                {t(`manager.aiAgents.calendar.permissions.${labelKey}.help`)}
              </p>
            )}
          </div>
        </div>
      )}
    />
  )
}

/** Un message (objet + corps, ou texto seul) avec les variables en pastilles « @ ».
 *  Vide = le texte par défaut, montré en placeholder dans la langue de l'agent. */
function MessageEditor({ control, titre, subjectName, bodyName, defaultSubject, defaultBody, variables, texto }: {
  control: Control<FormValues>
  titre: string
  subjectName?: 'calendar_cancellation_email_subject' | 'calendar_reschedule_email_subject'
  bodyName: 'calendar_cancellation_email_body' | 'calendar_reschedule_email_body' | 'calendar_reschedule_sms_template' | 'calendar_sms_template'
  defaultSubject?: string
  defaultBody: string
  variables: { id: string; name: string }[]
  texto?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{titre}</p>
      {subjectName && defaultSubject !== undefined && (
        <Controller
          control={control}
          name={subjectName}
          render={({ field }) => (
            <ZoneVariables
              value={versEcran(field.value ?? '', variables)}
              onChange={(v) => field.onChange(versServeur(v, variables))}
              colonnes={variables}
              placeholder={versEcran(defaultSubject, variables)}
              hint="manager.aiAgents.calendar.atHint"
            />
          )}
        />
      )}
      <Controller
        control={control}
        name={bodyName}
        render={({ field }) => (
          <ZoneVariables
            value={versEcran(field.value ?? '', variables)}
            onChange={(v) => field.onChange(versServeur(v, variables))}
            colonnes={variables}
            placeholder={versEcran(defaultBody, variables)}
            bulle={texto ? 'texto' : undefined}
            hint="manager.aiAgents.calendar.atHint"
          />
        )}
      />
      <p className="text-[11px] text-slate-400">{t('manager.aiAgents.calendar.templateEmptyHint')}</p>
    </div>
  )
}
